import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Check, Sparkles, Crown, Star, MessageCircle, Shield, Award, PhoneCall } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import {
  trackEvent,
  trackWhatsAppClick,
  trackBeginCheckout,
  trackPurchase,
  trackPaymentFailed,
} from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Tier catalog (mirrors backend TIER_PRICING + Poocho Guruji ₹99 quick tier) ───
// Feb 2026 CRO fix: ₹99 (`quick_99`) tier added so /booking?tier=quick_99 no longer
// silently defaults to ₹299. ₹99 tier is served via the /poocho-guruji flow
// (its own Razorpay path) — backend /api/booking/create-order does NOT accept
// quick_99 (source of truth). Selecting ₹99 on this page redirects the user to
// the correct Poocho Guruji checkout instead of hitting booking endpoint.
const TIERS = [
  {
    slug: 'quick_99',
    price: 99,
    label: 'Poocho Guruji Quick',
    labelHi: 'पूछो गुरुजी · Quick',
    tagline: '1 प्रश्न का व्यक्तिगत वैदिक उत्तर',
    delivery: 'WhatsApp पर ~10 min · तुरन्त deliver',
    features: [
      '1 प्रश्न का पराशरी उत्तर',
      'पराशरीय श्लोक + अर्थ',
      'Vedic उपाय सुझाव',
      'WhatsApp/Email पर तुरंत delivery',
      '100% Razorpay secure',
    ],
    accent: 'gold-solid',
    badge: 'सबसे तेज़',
    icon: MessageCircle,
    // ₹99 uses the Poocho Guruji Razorpay flow (see backend TIER_CONFIG).
    // We route the user there rather than call /api/booking/create-order.
    redirectTo: '/poocho-guruji#poocho-question-picker',
  },
  {
    slug: 'deep_299',
    price: 299,
    label: 'Deep Analysis',
    labelHi: 'गहन विश्लेषण',
    tagline: '3 प्रश्न + Dasha विश्लेषण',
    delivery: 'PDF Report + WhatsApp · ~24 hrs',
    features: [
      '3 प्रश्नों का विस्तृत उत्तर',
      'Vimshottari Dasha timing analysis',
      'Yogas और Doshas का mapping',
      'PDF रिपोर्ट + WhatsApp delivery',
      'FREE follow-up doubt clarification',
    ],
    accent: 'gold-solid',
    badge: 'Most Popular',
    icon: Sparkles,
  },
  {
    slug: 'premium_999',
    price: 999,
    label: 'Poocho Guruji Premium',
    labelHi: 'पूछो गुरुजी · Premium (Monthly)',
    tagline: '30-दिन Personalised Kundli-based AI Guidance',
    delivery: 'AI-guided daily · Dashboard + WhatsApp',
    features: [
      '30 दिनों तक प्रतिदिन 1 व्यक्तिगत ज्योतिषीय मार्गदर्शन',
      'वैदिक पाराशरीय ज्योतिष के शास्त्रीय सिद्धांतों के अनुसार',
      'Dasha, Gochar एवं Kundli आधारित उत्तर',
      'User की Kundli, प्रश्न एवं उत्तर History स्वतः सुरक्षित',
    ],
    accent: 'gold',
    badge: 'Best Value',
    icon: Star,
  },
  {
    slug: 'personal_5100',
    price: 5100,
    label: 'Personal Consultation',
    labelHi: 'व्यक्तिगत परामर्श',
    tagline: '90-min in-depth session',
    delivery: 'In-person Faridabad / Video call',
    features: [
      '90-मिनट In-person/Video session',
      'Complete life reading (Career/Marriage/Health)',
      'Full Varga analysis (D-9, D-10, D-7, D-20)',
      'Custom Yantra + Rudraksha guidance',
      '3-month WhatsApp follow-up',
    ],
    accent: 'royal',
    badge: null,
    icon: Crown,
  },
];

const MEDIA_LOGOS = [
  { name: 'Zee News', color: '#e91e63' },
  { name: 'Aaj Tak', color: '#c62828' },
  { name: 'ABP News', color: '#d32f2f' },
  { name: 'News18', color: '#ff5722' },
  { name: 'News Nation', color: '#0d47a1' },
];

const FAQS = [
  {
    q: 'Payment के बाद अगला step क्या होगा?',
    a: 'Successful payment ke turant baad hamari team WhatsApp par aap ko contact karegi (Deep Analysis के लिए ~24 ghante mein PDF report bhej di jaati hai). Premium/Personal tier ke लिए slot booking WhatsApp par confirm hoti hai.',
  },
  {
    q: 'क्या यह secure है? Razorpay live payment है?',
    a: 'Haan, ye 100% secure Razorpay Live Mode payment hai. RBI-approved gateway, PCI-DSS compliant, SSL encrypted. Aap ki payment details Razorpay के साथ direct handle hoti hain — humein sirf order confirmation milta hai.',
  },
  {
    q: 'Refund policy kya hai?',
    a: 'हर tier की अपनी refund policy है — Quick ₹99: uttar generate na hone par 2 ghante mein full refund. Deep Analysis ₹299: 48 ghante mein report na milne par full refund. Premium ₹999: pehle 24 ghante mein koi AI margdarshan na milne par full refund. Personal ₹5,100: session start hone se pehle 100% refund. पूरी policy पढ़ें: /refund-policy पर।',
  },
  {
    q: 'Kaunsa tier meri need ke liye best hai?',
    a: '1 quick question ke liye ₹99 Poocho Guruji, multi-question deep analysis ke liye ₹299, 30-दिन daily Poocho Guruji AI guidance ke liye ₹999, aur complete life reading + follow-up support ke liye ₹5,100 tier chunein.',
  },
  {
    q: 'Personal consultation kaha hoti hai?',
    a: 'Faridabad office (Sector 15, Haryana) mein in-person, ya video call (Zoom/Google Meet) par bhi ho sakti hai — aap ki convenience के हिसाब से.',
  },
  {
    q: 'Guruji ka experience kitna hai?',
    a: 'Acharya Vivek Mudgal ji ka 33+ saal ka Vedic astrology aur Vastu experience hai. Zee News, Aaj Tak, ABP, News18 par featured. 33,000+ successful consultations across India, UAE, USA.',
  },
];

const TESTIMONIALS = [
  { name: 'Ritu Sharma', city: 'Delhi', text: 'Guruji ne meri shaadi ka time bilkul sahi predict kiya. Deep Analysis report bahut detailed thi.', rating: 5 },
  { name: 'Ankit Verma', city: 'Faridabad', text: 'Premium consultation call ke baad career mein clarity aa gayi. Guruji ke remedies effective hain.', rating: 5 },
  { name: 'Priya Khanna', city: 'Gurugram', text: 'Personal 90-min session ne meri life ka har aspect cover kiya. Worth every rupee.', rating: 5 },
];

const cardStyle = (isPopular, selected) => {
  const base = 'relative flex flex-col rounded-2xl p-6 md:p-7 border-2 transition-all duration-300 cursor-pointer';
  if (selected) {
    return `${base} bg-gradient-to-br from-[#1a1230] via-[#0f0b25] to-[#0a0820] border-gold shadow-[0_0_40px_rgba(212,175,55,0.35)] scale-[1.02]`;
  }
  if (isPopular) {
    return `${base} bg-gradient-to-br from-[#1a1230] via-[#0f0b25] to-[#0a0820] border-gold/70 shadow-[0_0_28px_rgba(212,175,55,0.2)] hover:border-gold`;
  }
  return `${base} bg-[#0a0820]/70 border-gold/25 hover:border-gold/60 hover:-translate-y-1`;
};

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const initialTier = searchParams.get('tier') || 'deep_299';
  // Feb 2026 CRO fix: preserve the exact tier from URL — never silently
  // downgrade quick_99 → deep_299. Only fall back if slug truly unknown.
  const [selectedTier, setSelectedTier] = useState(
    TIERS.find((t) => t.slug === initialTier) ? initialTier : 'deep_299'
  );
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [processing, setProcessing] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const tier = useMemo(() => TIERS.find((t) => t.slug === selectedTier) || TIERS[0], [selectedTier]);

  // Ensure Razorpay checkout.js script is loaded on mount
  useEffect(() => {
    if (window.Razorpay) return;
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    document.body.appendChild(s);
  }, []);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const openWhatsApp = () => {
    const msg = encodeURIComponent(
      `नमस्ते Acharya ji, मैं ${tier.label} (₹${tier.price}) book करना चाहता/चाहती हूँ।`
    );
    try { trackWhatsAppClick('booking_page'); } catch (_) { /* noop */ }
    window.open(`https://wa.me/919311369729?text=${msg}`, '_blank');
  };

  const handleBook = async (e) => {
    e.preventDefault();

    // ═══ Feb 2026 CRO fix — ₹99 tier is served by /poocho-guruji flow ═══
    // Backend /api/booking/create-order only accepts deep_299/premium_999/
    // personal_5100 (source of truth). For quick_99 we redirect to the
    // Poocho Guruji question picker instead of falsely charging ₹299.
    if (tier.redirectTo) {
      try {
        trackEvent('booking_tier_redirect', {
          tier: tier.slug,
          price_inr: tier.price,
          target: tier.redirectTo,
        });
      } catch (_) { /* noop */ }
      window.location.assign(tier.redirectTo);
      return;
    }

    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('कृपया अपना नाम और phone number भरें');
      return;
    }
    if (form.phone.replace(/\D/g, '').length < 6) {
      toast.error('कृपया valid phone number भरें');
      return;
    }

    setProcessing(true);
    try {
      // Fire begin_checkout for GA4 funnel
      try {
        trackBeginCheckout({
          value: tier.price,
          currency: 'INR',
          item_name: tier.label,
          tier: tier.slug,
        });
      } catch (_) { /* noop */ }

      // 1. Create booking order
      const utm = (() => { try { return JSON.parse(localStorage.getItem('va_utm') || '{}'); } catch (_) { return {}; } })();
      const res = await axios.post(`${API}/api/booking/create-order`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        tier: tier.slug,
        message: form.message.trim(),
        utm,
      });
      const { booking_id, order_id, amount, currency, key_id, prefill, tier_label } = res.data;

      // 2. Open Razorpay checkout
      if (!window.Razorpay) {
        toast.error('Razorpay checkout load nahi ho paya, kuch der mein try karein');
        setProcessing(false);
        return;
      }

      const rzp = new window.Razorpay({
        key: key_id,
        amount,
        currency,
        order_id,
        name: 'ViratAstro',
        description: tier_label,
        image: 'https://viratastro.cloud/brand/va-cosmic-1024.png',
        theme: { color: '#D4AF37' },
        prefill: {
          name: prefill?.name || form.name,
          contact: prefill?.contact || form.phone,
          email: form.email,
        },
        handler: async (rzpResp) => {
          try {
            trackPurchase({
              transaction_id: rzpResp.razorpay_payment_id,
              value: tier.price,
              currency: 'INR',
              item_name: tier.label,
              tier: tier.slug,
            });
          } catch (_) { /* noop */ }

          try {
            await axios.post(`${API}/api/booking/verify-payment`, {
              booking_id,
              razorpay_order_id: rzpResp.razorpay_order_id,
              razorpay_payment_id: rzpResp.razorpay_payment_id,
              razorpay_signature: rzpResp.razorpay_signature,
            });
            trackEvent('booking_success', {
              tier: tier.slug,
              value: tier.price,
              currency: 'INR',
              transaction_id: rzpResp.razorpay_payment_id,
            });
            toast.success(`✅ Booking confirmed! WhatsApp par shortly contact karenge (Payment ID: ${rzpResp.razorpay_payment_id})`, { duration: 8000 });
            setForm({ name: '', phone: '', email: '', message: '' });
          } catch (verifyErr) {
            // Payment succeeded but verify failed — Razorpay webhook will still capture
            toast.warning('Payment mil gaya. Verification pending — hum WhatsApp par confirm karenge.');
          }
          setProcessing(false);
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
          },
        },
      });

      rzp.on('payment.failed', (fail) => {
        try {
          trackPaymentFailed({
            reason: fail?.error?.description,
            transaction_id: fail?.error?.metadata?.payment_id,
            value: tier.price,
          });
        } catch (_) { /* noop */ }
        toast.error(`Payment failed: ${fail?.error?.description || 'Please try again'}`);
        setProcessing(false);
      });

      rzp.open();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Something went wrong';
      toast.error(`Booking failed: ${detail}`);
      setProcessing(false);
    }
  };

  // FAQ JSON-LD for SEO
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-background text-text-main">
      <SEO
        title="Book Consultation | Acharya Vivek Mudgal | ViratAstro"
        description="Book your Vedic astrology consultation with Acharya Vivek Mudgal (33+ years). Deep Analysis ₹299, Poocho Guruji Premium ₹999 (30-day AI guidance), or Personal 90-min ₹5,100. Secure Razorpay payment."
        path="/booking"
        image="/brand/og/va-og-consultation.png"
        keywords="book astrology consultation, vedic consultation online, acharya vivek mudgal booking, kundli analysis, ज्योतिष परामर्श, गहन विश्लेषण"
        jsonLd={faqSchema}
      />
      <Header />

      {/* ──────── HERO ──────── */}
      <section className="relative pt-24 pb-10 md:pt-32 md:pb-14 bg-gradient-to-b from-[#020410] via-[#050820] to-[#030612]">
        <div className="container mx-auto px-4 max-w-6xl text-center">
          <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-gold font-semibold mb-3">
            Book Your Consultation · परामर्श बुक करें
          </p>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-medium tracking-tight mb-4">
            जीवन का हर सवाल — <span className="text-gold">Vedic answer मिलेगा</span>
          </h1>
          <p className="text-text-muted text-sm md:text-lg max-w-3xl mx-auto mb-6">
            Acharya Vivek Mudgal ji · 33+ वर्ष अनुभव · TV Featured · 33,000+ Consultations · 4.9★ Rating
          </p>
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 text-xs">
            {['Zee News','Aaj Tak','ABP News','News18','News Nation'].map((m) => (
              <span key={m} className="px-3 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold">
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ──────── TIER SELECTOR ──────── */}
      <section className="py-10 md:py-14" data-testid="booking-tier-selector">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-8">
            <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-gold font-semibold mb-2">
              Step 1 · Choose Your Tier
            </p>
            <h2 className="font-heading text-2xl md:text-4xl font-medium">
              अपने लिए सही <span className="text-gold">परामर्श चुनें</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {TIERS.map((t) => {
              const Icon = t.icon;
              const isPopular = t.badge === 'Most Popular';
              const isSelected = t.slug === selectedTier;
              return (
                <div
                  key={t.slug}
                  data-testid={`booking-tier-${t.slug}`}
                  onClick={() => {
                    setSelectedTier(t.slug);
                    try {
                      trackEvent('booking_tier_select', {
                        tier: t.slug,
                        price_inr: t.price,
                      });
                    } catch (_) { /* noop */ }
                  }}
                  className={cardStyle(isPopular, isSelected)}
                >
                  {t.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className={`inline-block px-3 py-1 rounded-full text-[10px] md:text-xs font-semibold tracking-widest uppercase ${
                        isPopular
                          ? 'bg-gradient-to-r from-[#F4D477] to-[#B8942D] text-[#0a0820] shadow-lg shadow-gold/40'
                          : 'bg-gold/15 text-gold border border-gold/40'
                      }`}>
                        {t.badge}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isPopular ? 'bg-gold/20' : 'bg-gold/10'}`}>
                      <Icon className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <div className="font-heading text-xl text-text-main leading-tight">{t.label}</div>
                      <div className="text-xs text-gold/70">{t.labelHi}</div>
                    </div>
                  </div>

                  <div className="mb-2">
                    <span className={`text-5xl font-heading font-bold leading-none ${isPopular ? 'text-transparent bg-clip-text bg-gradient-to-b from-[#FDE49B] via-[#F4D477] to-[#B8942D]' : 'text-gold'}`}>
                      ₹{t.price.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="text-sm text-text-main mb-1">{t.tagline}</div>
                  <div className="text-xs text-text-muted mb-4">📦 {t.delivery}</div>

                  <ul className="space-y-2 mb-2 flex-grow">
                    {t.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-text-main/90">
                        <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {isSelected && (
                    <div className="mt-4 text-center text-xs font-semibold tracking-widest uppercase text-gold">
                      ✓ Selected
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ──────── BOOKING FORM ──────── */}
      <section className="py-10 md:py-14 bg-gradient-to-b from-[#030612] via-[#050820] to-[#020410]" data-testid="booking-form-section">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="text-center mb-8">
            <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-gold font-semibold mb-2">
              Step 2 · Your Details
            </p>
            <h2 className="font-heading text-2xl md:text-4xl font-medium">
              सिर्फ 3 फील्ड — <span className="text-gold">तुरंत book करें</span>
            </h2>
          </div>

          {/* Selected tier summary */}
          <div className="mb-6 p-4 rounded-xl bg-gold/5 border border-gold/25 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-gold/70">Selected Tier</div>
              <div className="font-heading text-lg text-text-main">{tier.label}</div>
              <div className="text-xs text-text-muted">{tier.tagline}</div>
            </div>
            <div className="text-3xl font-heading font-bold text-gold">
              ₹{tier.price.toLocaleString('en-IN')}
            </div>
          </div>

          <form onSubmit={handleBook} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-gold/80 mb-1.5 font-semibold">
                आपका नाम *
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleFieldChange}
                required
                maxLength={80}
                className="w-full px-4 py-3 bg-[#0a0820]/70 border border-gold/30 rounded-lg text-text-main focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold placeholder:text-text-muted/60"
                placeholder="जैसे: विवेक शर्मा"
                data-testid="booking-name-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-gold/80 mb-1.5 font-semibold">
                WhatsApp Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleFieldChange}
                required
                maxLength={20}
                className="w-full px-4 py-3 bg-[#0a0820]/70 border border-gold/30 rounded-lg text-text-main focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold placeholder:text-text-muted/60"
                placeholder="+91 98XXXXXXXX"
                data-testid="booking-phone-input"
              />
              <div className="text-[11px] text-text-muted mt-1">📱 WhatsApp par confirmation aur updates milenge</div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-gold/80 mb-1.5 font-semibold">
                Email (optional)
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleFieldChange}
                maxLength={120}
                className="w-full px-4 py-3 bg-[#0a0820]/70 border border-gold/30 rounded-lg text-text-main focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold placeholder:text-text-muted/60"
                placeholder="you@example.com"
                data-testid="booking-email-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-gold/80 mb-1.5 font-semibold">
                आपका सबसे बड़ा सवाल? (optional)
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleFieldChange}
                rows={3}
                maxLength={800}
                className="w-full px-4 py-3 bg-[#0a0820]/70 border border-gold/30 rounded-lg text-text-main focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold placeholder:text-text-muted/60"
                placeholder="जैसे: विवाह कब होगा? Career growth कब आएगी?"
                data-testid="booking-message-input"
              />
            </div>

            <button
              type="submit"
              disabled={processing}
              className="w-full py-4 rounded-lg font-heading text-lg font-semibold bg-gradient-to-r from-[#F4D477] via-[#E5C158] to-[#B8942D] text-[#0a0820] hover:brightness-110 active:scale-[0.98] transition-all shadow-xl shadow-gold/25 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="booking-pay-btn"
            >
              {processing
                ? '⏳ Processing…'
                : tier.redirectTo
                  ? `अभी पूछो — ₹${tier.price} में उत्तर →`
                  : `🔒 Pay ₹${tier.price.toLocaleString('en-IN')} securely via Razorpay`}
            </button>

            <button
              type="button"
              onClick={openWhatsApp}
              className="w-full py-3 rounded-lg font-semibold border border-gold/50 text-gold hover:bg-gold hover:text-[#0a0820] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              data-testid="booking-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" /> या WhatsApp पर सीधे book करें
            </button>

            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-5 text-xs text-text-muted pt-2">
              <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-emerald-400" /> Secure Payment</span>
              <span className="flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-gold" /> 33+ Years</span>
              <span className="flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-gold" /> 4.9★ Rating</span>
              <Link to="/refund-policy" className="flex items-center gap-1.5 hover:text-gold transition-colors underline underline-offset-2" data-testid="booking-refund-policy-link">
                Refund Policy देखें
              </Link>
            </div>
          </form>
        </div>
      </section>

      {/* ──────── TESTIMONIALS ──────── */}
      <section className="py-10 md:py-14" data-testid="booking-testimonials">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-8">
            <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-gold font-semibold mb-2">
              Testimonials · ग्राहक अनुभव
            </p>
            <h2 className="font-heading text-2xl md:text-4xl font-medium">
              33,000+ लोग <span className="text-gold">Guruji पर विश्वास</span> करते हैं
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="p-6 rounded-2xl bg-[#0a0820]/70 border border-gold/25">
                <div className="flex mb-2">
                  {[...Array(t.rating)].map((_, j) => <Star key={j} className="w-4 h-4 text-gold fill-gold" />)}
                </div>
                <p className="text-sm text-text-main/90 italic mb-3">&ldquo;{t.text}&rdquo;</p>
                <div className="text-xs">
                  <span className="text-gold font-semibold">{t.name}</span>
                  <span className="text-text-muted"> · {t.city}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────── FAQ ──────── */}
      <section className="py-10 md:py-14 bg-gradient-to-b from-[#030612] to-[#020410]" data-testid="booking-faq">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-8">
            <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-gold font-semibold mb-2">
              FAQ · अक्सर पूछे जाने वाले सवाल
            </p>
            <h2 className="font-heading text-2xl md:text-4xl font-medium">
              कोई सवाल है? <span className="text-gold">यहाँ जवाब है</span>
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <div key={i} className="rounded-xl bg-[#0a0820]/70 border border-gold/20 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gold/5 transition-colors"
                  data-testid={`booking-faq-${i}`}
                >
                  <span className="font-medium text-text-main pr-4">{f.q}</span>
                  <span className={`text-gold text-xl transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-text-muted leading-relaxed">
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Fallback CTA */}
          <div className="mt-8 p-5 rounded-xl bg-gold/5 border border-gold/25 text-center">
            <p className="text-sm text-text-main mb-3">कोई और सवाल? WhatsApp पर सीधे Guruji से पूछें</p>
            <button
              onClick={openWhatsApp}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm transition-colors"
              data-testid="booking-faq-whatsapp"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp पर पूछें
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
